// src/pages/SalesLedger/components/SalesLedgerTable.js
import React, {useState} from "react";
import { Table } from "antd";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import _ from "lodash";
import "../../../components/common/bt.css";

const fmt = (num) =>
  Number(num || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const SalesLedgerTable = ({ printMode = false }) => {
  const { list, loading } = useSelector((state) => state.salesLedger);

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
  });


  const handleTableChange = (pagination) => {
    setPagination(pagination);
  };


  const ledger = list?.ledger || [];
  const openingBalance = list?.opening_balance || 0;
  const closingBalance = list?.closing_balance || 0;

  const debitTotal = _.sumBy(ledger, (r) => Number(r.debit_amount) || 0);
  const creditTotal = _.sumBy(ledger, (r) => Number(r.credit_amount) || 0);

  const ledgerWithSeq = ledger.map((r, i) => ({ ...r, seq: i + 1 }));

  const tableData = [
    { id: "opening", rowType: "opening", balance: openingBalance },
    ...ledgerWithSeq.map((row, index) => ({
      ...row,
      id: row.id || `ledger-${index}`,
      rowType: "ledger",
    })),
    {
      id: "totals",
      rowType: "totals",
      debit_total: debitTotal,
      credit_total: creditTotal,
    },
    { id: "closing", rowType: "closing", balance: closingBalance },
  ];

  const columns = [
    {
      title: "No.",
      key: "no",
      width: 60,
      render: (_, record) => {
        if (record.rowType === "ledger") return record.seq;
        if (record.rowType === "totals") {
          return "";
        }
        return null;
      },
    },
    {
      title: "Date",
      dataIndex: "created_date",
      render: (val, record) => {
        if (record.rowType === "opening") {
          return {
            children: "Opening Balance :",
            props: {
              colSpan: 4,
              style: { textAlign: "right", fontWeight: 700 },
            },
          };
        }
        if (record.rowType === "closing") {
          return {
            children: "Closing Balance :",
            props: {
              colSpan: 4,
              style: { textAlign: "right", fontWeight: 700 },
            },
          };
        }
        if (record.rowType === "totals") {
          return {
            children: "Grand Total :",
            props: {
              colSpan: 4, // span Particular, VCH No, VCH Type
              style: { textAlign: "right", fontWeight: 700 },
            },
          };
        }
        return val ? dayjs(val).format("DD-MMM-YYYY") : "";
      },
    },
    {
      title: "Particular",
      dataIndex: "particular",
      render: (val, r) =>
        r.rowType !== "ledger"
          ? { children: null, props: { colSpan: 0 } }
          : val,
    },
    {
      title: "Due Date",
      dataIndex: "due_date",
      render: (val, r) =>
        r.rowType !== "ledger"
          ? { children: null, props: { colSpan: 0 } }
          : typeof val === "string"
          ? val
          : val
          ? dayjs(val).format("DD-MMM-YYYY")
          : "",
    },
    {
      title: "VCH No",
      dataIndex: "vch_no",
      render: (val, r) =>
        r.rowType !== "ledger"
          ? { children: null, props: { colSpan: 0 } }
          : val,
    },
    {
      title: "VCH Type",
      dataIndex: "vch_type",
      render: (val, r) =>
        r.rowType !== "ledger"
          ? { children: null, props: { colSpan: 0 } }
          : val,
    },
    {
      title: "Debit",
      dataIndex: "debit_amount",
      align: "right",
      render: (val, record) => {
        if (["opening", "closing"].includes(record.rowType))
          return "";
        if (record.rowType === "totals")
          return <strong>{fmt(record.debit_total)}</strong>;
        return fmt(val);
      },
    },
    {
      title: "Credit",
      dataIndex: "credit_amount",
      align: "right",
      render: (val, record) => {
        if (["opening", "closing"].includes(record.rowType))
          return "";
        if (record.rowType === "totals")
          return <strong>{fmt(record.credit_total)}</strong>;
        return fmt(val);
      },
    },
    {
      title: "Balance",
      dataIndex: "balance",
      align: "right",
      render: (val, record) => {
        if (["opening", "closing"].includes(record.rowType))
          return <strong>{fmt(record.balance)}</strong>;
        if (record.rowType === "totals")
          return "";
        return fmt(val);
      },
    },
  ];

  if (printMode) {
    return (
      <table className="ledger-print-table">
       <colgroup>
      <col style={{ width: "4%" }} />   {/* No */}
      <col style={{ width: "9%" }} />   {/* Date */}
      <col style={{ width: "18%" }} />  {/* Particular */}
      <col style={{ width: "9%" }} />   {/* Due Date */}
      <col style={{ width: "25%" }} />  {/* VCH No */}
      <col style={{ width: "11%" }} />  {/* Debit */}
      <col style={{ width: "12%" }} />  {/* Credit */}
      <col style={{ width: "12%" }} />  {/* Balance */}
    </colgroup>
        <tbody>
          <tr className="ledger-print-heading">
            <td>No.</td>
            <td>Date</td>
            <td>Particular</td>
            <td>Due Date</td>
            <td>VCH No</td>
            <td style={{ textAlign: "right" }}>Debit</td>
            <td style={{ textAlign: "right" }}>Credit</td>
            <td style={{ textAlign: "right" }}>Balance</td>
          </tr>

          <tr className="ledger-opening-row">
            <td colSpan={7} style={{ textAlign: "right" }}>
              Opening Balance
            </td>
            <td style={{ textAlign: "right" }}>{fmt(openingBalance)}</td>
          </tr>

          {ledgerWithSeq.map((row, index) => (
            <tr key={row.id || index}>
              <td>{row.seq}</td>
              <td>{row.created_date ? dayjs(row.created_date).format("DD-MMM-YYYY") : "-"}</td>
              <td>{row.particular}</td>
              <td>
                {typeof row.due_date === "string"
                  ? row.due_date
                  : row.due_date
                  ? dayjs(row.due_date).format("DD-MMM-YYYY")
                  : ""}
              </td>
              <td>{row.vch_no}</td>
              <td style={{ textAlign: "right" }}>{fmt(row.debit_amount)}</td>
              <td style={{ textAlign: "right" }}>{fmt(row.credit_amount)}</td>
              <td style={{ textAlign: "right" }}>{fmt(row.balance)}</td>
            </tr>
          ))}

          <tr className="ledger-total-row">
            <td colSpan={6} style={{ textAlign: "right" }}>Grand Total</td>
            <td style={{ textAlign: "right" }}>{fmt(debitTotal)}</td>
            <td style={{ textAlign: "right" }}>{fmt(creditTotal)}</td>
            <td />
          </tr>

          <tr className="ledger-closing-row">
            <td colSpan={7} style={{ textAlign: "right" }}>
              Closing Balance
            </td>
            <td style={{ textAlign: "right" }}>{fmt(closingBalance)}</td>
          </tr>
        </tbody>
      </table>
    );
  }

  return (
    <Table
      rowKey="id"
      columns={columns}
      dataSource={tableData}
      loading={loading}
      pagination={{
        ...pagination,
        showSizeChanger: true,
        pageSizeOptions: ["10", "20", "50"],
      }}
      onChange={handleTableChange}
      bordered
      size={printMode ? "small" : "middle"}
      scroll={printMode ? undefined : { x: "max-content" }}
    />
  );
};

export default SalesLedgerTable;
